// App-wide toast notifications. A reactive singleton store any module can push
// to (e.g. `toastStore.error('…')`); the <Toasts /> component renders them.

export type ToastKind = 'error' | 'success' | 'info';

export type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
};

// Default time on screen (ms) per kind. Errors linger a little longer.
const DEFAULT_DURATION: Record<ToastKind, number> = {
  error: 6000,
  success: 4000,
  info: 4000
};

class ToastStore {
  toasts = $state<Toast[]>([]);

  #nextId = 0;

  // Page-provided render targets ("outlets"). A page mounts a <ToastOutlet /> to
  // pull toasts into its own layout (e.g. beside a button or at the end of a tab
  // strip). The most-recently mounted outlet wins; when none are mounted the
  // global <Toasts /> falls back to its fixed top-right position.
  #outlets = $state<number[]>([]);

  #nextOutletId = 0;

  // The outlet that should render the toasts, or null for the global default.
  get activeOutlet(): number | null {
    return this.#outlets.length ? this.#outlets[this.#outlets.length - 1] : null;
  }

  registerOutlet(): number {
    const id = this.#nextOutletId++;
    this.#outlets = [...this.#outlets, id];
    return id;
  }

  releaseOutlet(id: number) {
    this.#outlets = this.#outlets.filter((o) => o !== id);
  }

  // Show a toast; returns its id. `duration <= 0` keeps it until dismissed.
  // Only one toast is visible at a time: a new one replaces any still on screen,
  // which transitions out as the new one enters.
  add(message: string, kind: ToastKind = 'info', duration = DEFAULT_DURATION[kind]): number {
    const id = this.#nextId++;
    this.toasts = [{ id, kind, message }];

    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }

    return id;
  }

  error(message: string, duration?: number) {
    return this.add(message, 'error', duration);
  }

  success(message: string, duration?: number) {
    return this.add(message, 'success', duration);
  }

  info(message: string, duration?: number) {
    return this.add(message, 'info', duration);
  }

  remove(id: number) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }
}

export const toastStore = new ToastStore();
